import React, { useState } from "react";
import {
  Form,
  Input,
  Select,
  Typography,
  Button,
  Alert,
  Divider,
  Checkbox,
} from "antd";
import Modal from "react-modal";
import type { UserModel } from "../../models/UserModel";
import { toast } from "react-toastify";
import axios from "../../api/axios";
import { fetchPostalCodeDetails } from "../../utils/functions/fetchPostalCodeDetails";
import { isValidRcs } from "../../utils/functions/validateRCSNumber";
import { isValidPostalCode } from "../../utils/functions/validatePostalCode";
import { motion, AnimatePresence } from "framer-motion";
import "../style/payement.css";
import "../style/UserValidationModal.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { saveUserToLocalStorage } from "../../services/Login_service";
import {
  faUser,
  faBuilding,
  faBriefcase,
  faCheckCircle,
  faBank,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import ProfessionalDocumentUpload from "../register/ProfessionalDocumentUpload";
import RibModal from "./avoir/RibModal";
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch } from "react-redux";
import { setUser } from '../../userSlice';

const { Title, Text } = Typography;

interface AnonymousModalPaiementProps {
  isModalVisible: boolean;
  userDetails: UserModel | null;
  setUserDetails: React.Dispatch<React.SetStateAction<UserModel | null>>;
  handleModalClose: () => void;
  handlePayment?: () => Promise<void>;
  onRegistrationComplete?: (user: UserModel) => void;
  isDraft: boolean;
}

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" as const },
  },
};



const PROFESSION_OPTIONS: Record<string, string[]> = {
  "Professions juridiques": [
    "Administrateur / Mandataire judiciaire",
    "Avocat",
    "Clerc / Formaliste de notaire",
    "Greffier / Assistant de greffe",
    "Huissier de justice / Commissaire de justice",
    "Juriste d'entreprise",
    "Notaire",
  ],
  "Professions comptables": [
    "Cabinet comptable / Conseil en gestion",
    "Commissaire aux comptes",
    "Expert-comptable",
    "Gestionnaire de paie / RAF",
  ],
  "Créateurs et dirigeants d'entreprise": [
    "Dirigeant d'entreprise",
    "Entrepreneur individuel / Auto-entrepreneur",
    "Office manager / Responsable administratif",
  ],
  "Intermédiaires et formalistes": [
    "Agence de communication / Publicité",
    "Cabinet de formalités juridiques",
    "Formaliste en annonces légales",
    "Secrétaire juridique indépendant",
    "Société de domiciliation",
  ],
  "Autres professions": [
    "Administration / Assistance administrative",
    "Ressources humaines",
    "Responsable juridique",
    "Consultant indépendant",
  ],
  "Autre": ["Autre (à préciser)"],
};

const generateProfessionOptions = () => {
  const options: { value: string; label: string; isCategory?: boolean }[] = [];
  Object.entries(PROFESSION_OPTIONS).forEach(([category, subcategories]) => {
    options.push({
      value: `__category__${category}`,
      label: category.toUpperCase(),
      isCategory: true,
    });
    subcategories.forEach((sub) => {
      options.push({
        value: sub.includes("Autre") ? "Autre" : sub,
        label: `  ${sub}`,
      });
    });
  });
  return options;
};

const professionOptions = generateProfessionOptions();

const AnonymousModalPaiement: React.FC<AnonymousModalPaiementProps> = ({
  isModalVisible,
  handleModalClose,
  userDetails,
  setUserDetails,
  handlePayment,
  onRegistrationComplete,
  isDraft = false ,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState<string[]>([]);
  const [form] = Form.useForm();
  
  const getUserId = () => {
    if (userDetails?.id) return userDetails.id;
    const localUserId = localStorage.getItem("userId");
    return localUserId ? parseInt(localUserId, 10) : null;
  };
  
  const user = userDetails as UserModel;
  const userId = getUserId();

  const initialCategory =
    userDetails?.user_type === "entreprise"
      ? "entreprise"
      : userDetails?.user_type === "professionnel"
      ? "professionnel"
      : "particulier";

  const [selectedCategory, setSelectedCategory] = useState<string>(
    form.getFieldValue("user_category") || initialCategory
  );

  const [professionalDocuments, setProfessionalDocuments] = useState({
    kbisFile: null as File | null,
    identityRecto: null as File | null,
    identityVerso: null as File | null,
    identityType: "cin" as "cin" | "passport" | "other",
    ribFile: null as File | null,
  });

  const [bankCoordinates, setBankCoordinates] = useState({
    account_number: "",
    bic_swift: "",
  });

  const [ribModalVisible, setRibModalVisible] = useState<boolean>(false);

  const [showSuccessCard, setShowSuccessCard] = useState(false);
  const [registrationData, setRegistrationData] = useState<{
    email: string;
    password: string;
  } | null>(null);

  const [feedback, setFeedback] = useState<{
  type: "success" | "error" | "info" | "warning";
  message: string;
} | null>(null);


  const handlePostalCodeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const postal_code = e.target.value;

    if (/^\d{0,5}$/.test(postal_code)) {
      form.setFieldsValue({ postal_code });

      if (isValidPostalCode(postal_code)) {
        try {
          const postalDetails = await fetchPostalCodeDetails(postal_code);
          const citiesList = postalDetails?.cities || [];
          setCities(citiesList);

          if (citiesList.length > 0) {
            form.setFieldsValue({ country: citiesList[0] });
          }
        } catch (err) {
          console.error(err);
          setCities([]);
        }
      } else {
        setCities([]);
        form.setFieldsValue({ country: "" });
      }
    }
  };

  const handleSIRENInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const code_SIREN = e.target.value;

    if (/^\d{0,9}$/.test(code_SIREN)) {
      form.setFieldsValue({ code_SIREN });

      if (isValidRcs(code_SIREN)) {
        try {
          const response = await axios(`/rne/${code_SIREN}`);
          const nafCode = await axios(`/rne/naf/${code_SIREN}`);

          if (response.data) {
            const postalCode = response.data.data.postalCode;

            form.setFieldsValue({
              raison_social: response.data.data.companyName,
              adresse: response.data.data.address,
              postal_code: postalCode,
              country: response.data.data.cities?.[0] || "",
              code_NAF: nafCode.data.data.naf_code,
              tv_intra: nafCode.data.data.vat_number,
            });

            if (postalCode && isValidPostalCode(postalCode)) {
              const postalDetails = await fetchPostalCodeDetails(postalCode);
              setCities(postalDetails?.cities || []);
            } else {
              setCities(response.data.data.cities || []);
            }
          }
        } catch (err) {
          console.error(err);
        }
      }
    }
  };

  const handleProfessionalDocuments = (docs: any) => {
    setProfessionalDocuments((prev) => ({
      ...prev,
      ...docs,  
    }));
  };

  const handleRibConfirm = (rib: string, bic?: string) => {
    setBankCoordinates({
      account_number: rib,
      bic_swift: bic || "",
    });

    form.setFieldsValue({
      account_number: rib,
      bic_swift: bic || "",
    });

    setRibModalVisible(false);
    setFeedback({
      type: "success",
      message: "✅ Coordonnées bancaires enregistrées",
    });
  };

  const handleFinish = async (values: any) => {
    setLoading(true);

    try {
      const category = values.user_category || selectedCategory || "particulier";
      // console.log("📋 Catégorie sélectionnée:", category);
      
      let user_type = "personne";

      if (category === "particulier") {
        user_type = "personne";
      } else if (category === "entreprise") {
        user_type = "entreprise";
      } else if (category === "professionnel") {
        // console.log("🔒 Mode professionnel - Début des validations");
        
        if (!professionalDocuments.kbisFile) {
          console.error("❌ BLOCAGE: Kbis manquant");
          setFeedback({
            type: "error",
            message: "⚠️ Veuillez uploader l'extrait Kbis",
          });
          setLoading(false);
          return;
        }

        if (!professionalDocuments.identityRecto) {
          console.error("❌ BLOCAGE: Pièce d'identité (recto) manquante");
          toast.error("⚠️ Veuillez uploader la pièce d'identité (recto)");
          setLoading(false);
          return;
        }

        if (
          professionalDocuments.identityType === "cin" &&
          !professionalDocuments.identityVerso
        ) {
          console.error("❌ BLOCAGE: Verso de la CIN manquant");
          toast.error("⚠️ Veuillez uploader le verso de la carte d'identité");
          setLoading(false);
          return;
        }

        if (!professionalDocuments.ribFile) {
          console.error("❌ BLOCAGE: Fichier RIB manquant");
          toast.error("⚠️ Veuillez uploader le document RIB (PDF ou image) via la section 'Documents professionnels'");
          setLoading(false);
          return;
        }

        const accountNumber = values.account_number || bankCoordinates.account_number;
        const bicSwift = values.bic_swift || bankCoordinates.bic_swift;

        // console.log("💳 Vérification coordonnées bancaires:", {
        //   account_number: accountNumber,
        //   bic_swift: bicSwift,
        // });

        if (!accountNumber || !bicSwift) {
          console.error("❌ BLOCAGE: Coordonnées bancaires manquantes");
          toast.error("⚠️ Veuillez renseigner vos coordonnées bancaires (RIB + BIC) via le bouton 'Ajouter les coordonnées bancaires'");
          setLoading(false);
          return;
        }

        user_type = "professionnel";
        // console.log("✅ Toutes les validations professionnelles passées");
      }

      // console.log("📦 Préparation du FormData");
      const formData = new FormData();
      
      const fieldsToAdd = {
        firstname: values.firstname || "",
        name: values.name || "",
        email: values.email || "",
        number: values.number || "",
        adresse: values.adresse || "",
        postal_code: values.postal_code || "",
        country: values.country || "",
        civility: values.civility || "",
        profession: values.profession === "Autre" ? "autre" : values.profession,
      };

      Object.entries(fieldsToAdd).forEach(([key, value]) => {
        formData.append(key, value);
        // console.log(`  ✓ ${key}: ${value}`);
      });

      if (values.profession === "Autre" && values.profession_other) {
        formData.append("profession_is_other", "true");
        formData.append("profession_other", values.profession_other);
        // console.log("  ✓ profession_other:", values.profession_other);
      }

      formData.append("user_type", user_type);
      formData.append("is_entreprise", String(user_type !== "personne"));
      formData.append('show_in_directory', values.show_in_directory ? 'true' : 'false');

      // console.log(`  ✓ user_type: ${user_type}`);

      if (user_type === "entreprise" || user_type === "professionnel") {
        formData.append("code_SIREN", values.code_SIREN || "");
        formData.append("raison_social", values.raison_social || "");
        formData.append("code_NAF", values.code_NAF || "");
        formData.append("tv_intra", values.tv_intra || "");
        // console.log("  ✓ Informations entreprise ajoutées");
      }

      if (values.newPassword) {
        formData.append("password", values.newPassword);
        formData.append("confirmPassword", values.confirmPassword);
        // console.log("  ✓ Mot de passe ajouté");
      }

      if (user_type === "professionnel") {
        // console.log("📄 Ajout des documents au FormData:");

        formData.append("kbis_document", professionalDocuments.kbisFile!);
        // console.log(`  ✓ kbis_document: ${professionalDocuments.kbisFile!.name}`);

        formData.append("identity_document_recto", professionalDocuments.identityRecto!);
        // console.log(`  ✓ identity_document_recto: ${professionalDocuments.identityRecto!.name}`);

        if (professionalDocuments.identityVerso) {
          formData.append("identity_document_verso", professionalDocuments.identityVerso);
          // console.log(`  ✓ identity_document_verso: ${professionalDocuments.identityVerso.name}`);
        }

        formData.append("identity_document_type", professionalDocuments.identityType);
        // console.log(`  ✓ identity_document_type: ${professionalDocuments.identityType}`);

        formData.append("rib_document", professionalDocuments.ribFile!);
        // console.log(`  ✓ rib_document (FILE): ${professionalDocuments.ribFile!.name}`);

        const accountNumber = values.account_number || bankCoordinates.account_number;
        const bicSwift = values.bic_swift || bankCoordinates.bic_swift;

        formData.append("account_number", accountNumber);
        formData.append("bic_swift", bicSwift);
        // console.log(`  ✓ account_number (STRING): ${accountNumber}`);
        // console.log(`  ✓ bic_swift (STRING): ${bicSwift}`);

        formData.append("status", "waiting_validation");
        formData.append("is_validated", "false");
        formData.append("documents_verified", "false");
        // console.log("  ✓ Statuts de validation ajoutés");
      } else {
        formData.append("status", "active");
        // console.log("  ✓ status: active");
      }

      // console.log(`📤 Envoi des données vers /user/${userId}/update-with-files`);

      const response = await axios.put(
        `/user/${userId}/update-with-files`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      // console.log("✅ Réponse du serveur:", response.data);

      if (user_type === "professionnel") {
        setProfessionalDocuments({
          kbisFile: null,
          identityRecto: null,
          identityVerso: null,
          identityType: "cin",
          ribFile: null,
        });
        setBankCoordinates({ account_number: "", bic_swift: "" });
        // console.log("🧹 Documents et coordonnées réinitialisés");
      }

        // En prod, la forme observée est : { message, data: { ...user }, modifiedFields: [...] }
      const rawData = response?.data;
      const returnedUser =
        rawData?.data ||      // cas le plus probable: user dans "data"
        rawData?.user ||      // fallback: user directement dans "user"
        rawData;              // dernier recours: tout l'objet (évite undefined)

      const returnedToken =
        rawData?.token || localStorage.getItem("token") || "";

      if (returnedUser) {
        try {
          // On ne tente la sauvegarde que si un id est présent pour éviter les erreurs toString()
          if (typeof (returnedUser as any).id === "number") {
            saveUserToLocalStorage(returnedUser as UserModel, returnedToken);
            dispatch(setUser(returnedUser as UserModel));
          } else {
            console.warn(
              "Utilisateur retourné sans id, sauvegarde localStorage ignorée:",
              returnedUser
            );
          }

          setUserDetails(returnedUser as UserModel);
        } catch (err) {
          console.warn(
            "Impossible de sauvegarder l'utilisateur dans localStorage:",
            err
          );
          setUserDetails({
            ...user,
            ...values,
            user_type,
            is_entreprise: user_type !== "personne",
          });
        }
      }

      if (handlePayment) {
        console.log("💳 Déclenchement du paiement...");
        await handlePayment();
      }

      // if (onRegistrationComplete) {
      //   onRegistrationComplete(  );
      // }

      if (onRegistrationComplete) {
        onRegistrationComplete(returnedUser as UserModel);
      }

     setRegistrationData({
        email: values.email,
        password: values.newPassword || "celui que vous venez de définir",
      });
      setShowSuccessCard(true);


    } catch (err: any) {
      console.error("❌ ERREUR lors de l'enregistrement:", err);
      console.error("❌ Détails de l'erreur:", err.response?.data);

      let errorMessage = "Erreur lors de l'enregistrement";

      const status = err?.response?.status;
      const data = err?.response?.data;

      if (status === 409 && data?.code === "EMAIL_ALREADY_EXISTS") {
        errorMessage = data.message || "Cet email est déjà utilisé";
      }

      else if (status === 409) {
        errorMessage = data?.message || "Conflit de données";
      }

      else if (status === 400) {
        errorMessage =
          data?.message ||
          "Données invalides. Veuillez vérifier vos informations.";
      }

      else if (status === 500) {
        errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
      }

      // 🧠 fallback
      else if (data?.message) {
        errorMessage = data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setFeedback({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };


  const formatRIB = (rib: string) => {
    const clean = rib.replace(/\s+/g, "");
    if (clean.length !== 23) return rib;
    const bank = clean.slice(0, 5);
    const guichet = clean.slice(5, 10);
    const compte = clean.slice(10, 21);
    const cle = clean.slice(21);
    return `${bank} ${guichet} ${compte} ${cle}`;
  };

  const renderAccountTypeCard = (
    type: string,
    title: string,
    description: string,
    icon: React.ReactNode
  ) => {
    const selectedTypeFromForm = form.getFieldValue("user_category");
    const isSelected = selectedCategory === type || selectedTypeFromForm === type;

    const handleClick = () => {
      form.setFieldsValue({ user_category: type });
      setSelectedCategory(type);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleClick();
      }
    };

    return (
      <motion.div
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleClick}
      >
        <div
          role="button"
          tabIndex={0}
          onKeyDown={onKeyDown}
          style={{
            border: `2px solid ${isSelected ? "#4BB0C0" : "#f0f0f0"}`,
            backgroundColor: isSelected ? "rgba(75, 176, 192, 0.04)" : "#fff",
            cursor: "pointer",
            height: "100%",
            transition: "all 0.25s ease",
            position: "relative",
            padding: "24px",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "2rem",
              color: isSelected ? "#4BB0C0" : "#8c8c8c",
              marginBottom: "12px",
              transition: "color 0.25s ease",
            }}
          >
            {icon}
          </div>
          <h4
            style={{
              color: isSelected ? "#4BB0C0" : "#262626",
              marginBottom: 8,
              margin: 0,
            }}
          >
            {title}
          </h4>
          <p style={{ fontSize: "0.85rem", color: "#666", margin: 0 }}>
            {description}
          </p>

          {isSelected && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.18 }}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                color: "#4BB0C0",
                fontSize: "1.2rem",
              }}
            >
              ✓
            </motion.div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <>
      <RibModal
        visible={ribModalVisible}
        onCancel={() => setRibModalVisible(false)}
        onConfirm={handleRibConfirm}
        requireBic={true}
      />

      <AnimatePresence>
        {isModalVisible && !showSuccessCard && (
          <Modal
            isOpen={isModalVisible}
            onRequestClose={handleModalClose}
            contentLabel="Finalisez votre inscription"
            className="user-validation-modal anonymous-modal-paiement"
            shouldCloseOnOverlayClick={false}
            overlayClassName="user-validation-overlay"
          >
            <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
              {/* En-tête du modal */}
              <div className="modal-header-custom">
                <div className="header-content">
                  <div className="user-avatar">
                    <i className="bi bi-person-circle"></i>
                  </div>
                  <div className="header-info">
                      <h2 className="modal-title">
                        {isDraft ? "Sauvegarder mes informations" : "Finalisez votre inscription"}
                      </h2>
                    <div className="user-meta">
                      <span> {isDraft ?"Complétez vos informations pour pouvoir sauvegarder votre annonce " : "Complétez vos informations pour obtenir l'attestation et la facture"}</span>
                    </div>
                  </div>
                </div>
                <button
                  className="modal-close-btn"
                  onClick={handleModalClose}
                  style={{
                    background: "none",
                    border: "none",
                    color: "white",
                    fontSize: "1.5rem",
                    cursor: "pointer",
                    padding: "0",
                  }}
                >
                  ✕
                </button>
              </div>


              {userDetails || userId ? (
                <motion.div variants={containerVariants} initial="hidden" animate="show" exit="hidden">
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFinish}
                    className="modern-form"
                  >
                    {/* Champs cachés */}
                    <Form.Item name="user_category" style={{ display: "none" }}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="account_number" style={{ display: "none" }}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="bic_swift" style={{ display: "none" }}>
                      <Input />
                    </Form.Item>

                    {/* Section 1 : Type de compte */}
                    <motion.div variants={itemVariants} className="info-section">
                      <div className="section-header">
                        <i className="bi bi-person-lines-fill"></i>
                        <h3>1. Sélectionnez votre type de compte</h3>
                      </div>
                      <div className="info-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                        <div className="info-item">
                          {renderAccountTypeCard("particulier", "Particulier", "Pour un usage personnel", <FontAwesomeIcon icon={faUser} />)}
                        </div>
                        <div className="info-item">
                          {renderAccountTypeCard("entreprise", "Entreprise", "Pour les sociétés et entreprises", <FontAwesomeIcon icon={faBuilding} />)}
                        </div>
                        <div className="info-item">
                          {renderAccountTypeCard("professionnel", "Professionnel", "Pour les professionnels du droit", <FontAwesomeIcon icon={faBriefcase} />)}
                        </div>
                      </div>
                    </motion.div>

                    {/* Section informations d'entreprise */}
                    <Form.Item shouldUpdate={(prev, curr) => prev.user_category !== curr.user_category}>
                      {({ getFieldValue }) =>
                        (getFieldValue("user_category") === "entreprise" || getFieldValue("user_category") === "professionnel") && (
                          <motion.div variants={itemVariants} className="info-section">
                            <div className="section-header">
                              <i className="bi bi-building"></i>
                              <h3>Informations de l'entreprise</h3>
                            </div>
                            <Alert
                              message="Renseignez les informations de votre entreprise"
                              type="info"
                              showIcon
                              icon={<FontAwesomeIcon icon={faInfoCircle} />}
                              style={{ marginBottom: 16 }}
                            />
                            <div className="info-grid">
                              <div className="info-item">
                                <div className="info-label">
                                  <i className="bi bi-credit-card me-2"></i>
                                  Code SIREN
                                </div>
                                <Form.Item name="code_SIREN" rules={[{ required: true, message: "Le code SIREN est requis" }]} style={{ marginBottom: 0 }}>
                                  <Input placeholder="123456789" onChange={handleSIRENInput} size="large" />
                                </Form.Item>
                              </div>

                              <div className="info-item">
                                <div className="info-label">
                                  <i className="bi bi-building me-2"></i>
                                  Raison sociale
                                </div>
                                <Form.Item name="raison_social" rules={[{ required: true, message: "La raison sociale est requise" }]} style={{ marginBottom: 0 }}>
                                  <Input placeholder="Nom de l'entreprise" size="large" />
                                </Form.Item>
                              </div>

                              <div className="info-item">
                                <div className="info-label">
                                  <i className="bi bi-file-earmark-text me-2"></i>
                                  Code NAF
                                </div>
                                <Form.Item name="code_NAF" rules={[{ required: true, message: "Le code NAF est requis" }]} style={{ marginBottom: 0 }}>
                                  <Input placeholder="62.01Z" size="large" />
                                </Form.Item>
                              </div>

                              <div className="info-item">
                                <div className="info-label">
                                  <i className="bi bi-receipt me-2"></i>
                                  N° TVA Intra
                                </div>
                                <Form.Item name="tv_intra" rules={[{ required: true, message: "Le numéro de TVA est requis" }]} style={{ marginBottom: 0 }}>
                                  <Input placeholder="FR12345678901" size="large" />
                                </Form.Item>
                              </div>
                            </div>
                          </motion.div>
                        )
                      }
                    </Form.Item>

                  {/* Section Annuaire professionnel */}
                    <Form.Item shouldUpdate={(prev, curr) => prev.user_category !== curr.user_category}>
                      {({ getFieldValue, setFieldsValue }) =>
                        (getFieldValue("user_category") === "professionnel" || getFieldValue("user_category") === "entreprise") && (
                          <motion.div
                            variants={itemVariants}
                            className="info-section"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                          >
                            <div className="section-header">
                              <i className="bi bi-eye"></i>
                              <h3>Annuaire professionnel</h3>
                            </div>

                            <div className="section-content">

                              {/* ── Consentement RGPD ── */}
                              <Form.Item name="rgpd_directory_consent" valuePropName="checked" initialValue={false} style={{ marginBottom: 12 }}>
                                <div style={{
                                  padding: '16px 20px',
                                  border: `1.5px solid ${getFieldValue("rgpd_directory_consent") ? '#2A919D' : '#e8e8e8'}`,
                                  borderRadius: 12,
                                  background: getFieldValue("rgpd_directory_consent") ? '#f0fafa' : '#fafafa',
                                  transition: 'all 0.3s ease'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontWeight: 600 }}>
                                    <i className="bi bi-shield-check" style={{ color: '#2A919D', fontSize: 18 }}></i>
                                    <span>Consentement RGPD requis</span>
                                    {getFieldValue("rgpd_directory_consent") && (
                                      <span className="badge ms-2" style={{ backgroundColor: '#2A919D' }}>✓ Accepté</span>
                                    )}
                                  </div>

                                  <p style={{ fontSize: 13, color: '#666', marginBottom: 14, lineHeight: 1.6 }}>
                                    Avant d'apparaître dans l'annuaire professionnel, vous devez accepter notre politique
                                    de confidentialité concernant la collecte et l'utilisation de vos données personnelles.
                                  </p>

                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                    <Checkbox
                                      id="rgpd_directory_consent_modal"
                                      onChange={(e) => {
                                        const accepted = e.target.checked;
                                        form.setFieldsValue({
                                          rgpd_directory_consent: accepted,
                                          show_in_directory: accepted ? form.getFieldValue("show_in_directory") : false,
                                        });
                                      }}
                                      style={{ flexShrink: 0, marginTop: 2, cursor: 'pointer' }}
                                    >
                                      J'ai lu et j'accepte la{' '}
                                      <a
                                        href="https://annonce-legale-officielle.fr/politique-de-confidentialite/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ color: '#2A919D', fontWeight: 600, textDecoration: 'none' }}
                                      >
                                        politique de confidentialité (RGPD)
                                      </a>
                                      {' '}et j'accepte que mes informations professionnelles soient visibles dans l'annuaire.
                                    </Checkbox>
                                  </div>
                                </div>
                              </Form.Item>

                              {/* ── Visibilité annuaire — grisé si RGPD non accepté ── */}
                              <Form.Item shouldUpdate={(prev, curr) => prev.rgpd_directory_consent !== curr.rgpd_directory_consent} noStyle>
                                {({ getFieldValue }) => (
                                  <Form.Item name="show_in_directory" valuePropName="checked" initialValue={false} style={{ marginBottom: 0 }}>
                                    <div style={{
                                      opacity: getFieldValue("rgpd_directory_consent") ? 1 : 0.45,
                                      pointerEvents: getFieldValue("rgpd_directory_consent") ? 'auto' : 'none',
                                      transition: 'opacity 0.3s ease',
                                      cursor: getFieldValue("rgpd_directory_consent") ? 'auto' : 'not-allowed',
                                      padding: '14px 20px',
                                      border: '1.5px solid #e8e8e8',
                                      borderRadius: 12,
                                      background: '#fafafa',
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <Checkbox
                                          id="show_in_directory_modal"
                                          disabled={!getFieldValue("rgpd_directory_consent")}
                                          onChange={(e) => form.setFieldsValue({ show_in_directory: e.target.checked })}
                                          style={{ cursor: getFieldValue("rgpd_directory_consent") ? 'pointer' : 'not-allowed' }}
                                        >
                                          Apparaître dans l'annuaire professionnel
                                        </Checkbox>
                                      </div>
                                      <small style={{ display: 'block', marginTop: 8, fontSize: 12, color: '#888' }}>
                                        {getFieldValue("show_in_directory") && getFieldValue("rgpd_directory_consent")
                                          ? "✓ Votre profil sera visible dans l'annuaire des professionnels."
                                          : getFieldValue("rgpd_directory_consent")
                                            ? "Votre profil sera masqué de l'annuaire."
                                            : "Acceptez la politique de confidentialité pour activer cette option."}
                                      </small>
                                    </div>
                                  </Form.Item>
                                )}
                              </Form.Item>

                            </div>
                          </motion.div>
                        )
                      }
                    </Form.Item>

                    {/* Section 2 : Informations personnelles */}
                    <motion.div variants={itemVariants} className="info-section">
                      <div className="section-header">
                        <i className="bi bi-person-lines-fill"></i>
                        <h3>2. Informations personnelles</h3>
                      </div>
                      <div className="info-grid">
                        <div className="info-item">
                          <div className="info-label">
                            <i className="bi bi-person-vcard me-2"></i>
                            Nom
                          </div>
                          <Form.Item name="name" rules={[{ required: true, message: "Le nom est requis" }]} style={{ marginBottom: 0 }}>
                            <Input placeholder="Votre nom" size="large" />
                          </Form.Item>
                        </div>

                        <div className="info-item">
                          <div className="info-label">
                            <i className="bi bi-person me-2"></i>
                            Prénom
                          </div>
                          <Form.Item name="firstname" rules={[{ required: true, message: "Le prénom est requis" }]} style={{ marginBottom: 0 }}>
                            <Input placeholder="Votre prénom" size="large" />
                          </Form.Item>
                        </div>

                        <div className="info-item">
                          <div className="info-label">
                            <i className="bi bi-gender-ambiguous me-2"></i>
                            Civilité
                          </div>
                          <Form.Item name="civility" rules={[{ required: true, message: "La civilité est requise" }]} style={{ marginBottom: 0 }}>
                            <Select placeholder="Sélectionnez" size="large">
                              <Select.Option value="monsieur">Monsieur</Select.Option>
                              <Select.Option value="madame">Madame</Select.Option>
                              <Select.Option value="autre">Autre</Select.Option>
                            </Select>
                          </Form.Item>
                        </div>

                        <div className="info-item">
                          <div className="info-label">
                            <i className="bi bi-envelope-at me-2"></i>
                            Email
                          </div>
                          <Form.Item name="email" rules={[{ required: true, type: "email", message: "Un email valide est requis" }]} style={{ marginBottom: 0 }}>
                            <Input placeholder="votre@email.com" size="large" />
                          </Form.Item>
                        </div>

                        <div className="info-item">
                          <div className="info-label">
                            <i className="bi bi-telephone me-2"></i>
                            Téléphone
                          </div>
                          <Form.Item name="number" rules={[{ required: true, message: "Le téléphone est requis" }]} style={{ marginBottom: 0 }}>
                            <Input placeholder="+33 1 23 45 67 89" size="large" />
                          </Form.Item>
                        </div>

                        <div className="info-item">
                          <div className="info-label">
                            <i className="bi bi-house-door me-2"></i>
                            Adresse
                          </div>
                          <Form.Item name="adresse" rules={[{ required: true, message: "L'adresse est requise" }]} style={{ marginBottom: 0 }}>
                            <Input placeholder="Adresse complète" size="large" />
                          </Form.Item>
                        </div>

                        <div className="info-item">
                          <div className="info-label">
                            <i className="bi bi-mailbox me-2"></i>
                            Code postal
                          </div>
                          <Form.Item name="postal_code" rules={[{ required: true, message: "Le code postal est requis" }]} style={{ marginBottom: 0 }}>
                            <Input placeholder="75001" onChange={handlePostalCodeChange} size="large" />
                          </Form.Item>
                        </div>

                        <div className="info-item">
                          <div className="info-label">
                            <i className="bi bi-geo-alt me-2"></i>
                            Ville
                          </div>
                          <Form.Item name="country" rules={[{ required: true, message: "La ville est requise" }]} style={{ marginBottom: 0 }}>
                            <Select placeholder="Sélectionnez une ville" size="large" loading={cities.length === 0}>
                              {cities.map((city) => (
                                <Select.Option key={city} value={city}>
                                  {city}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </div>

                        <div className="info-item">
                          <div className="info-label">
                            <i className="bi bi-briefcase me-2"></i>
                            Profession
                          </div>
                          <Form.Item name="profession" rules={[{ required: true, message: "La profession est requise" }]} style={{ marginBottom: 0 }}>
                            <Select placeholder="Sélectionnez votre profession" size="large" dropdownStyle={{ maxHeight: 300 }}>
                              {professionOptions.map((option, idx) => (
                                <Select.Option
                                  key={idx}
                                  value={option.value}
                                  disabled={option.isCategory}
                                  style={{
                                    fontWeight: option.isCategory ? "600" : "normal",
                                    backgroundColor: option.isCategory ? "#f6f6f6" : "white",
                                    paddingLeft: option.isCategory ? "12px" : "24px",
                                    color: option.isCategory ? "#4BB0C0" : "inherit",
                                  }}
                                >
                                  {option.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </div>
                      </div>
                    </motion.div>

                    {/* Section 3 : Sécurité du compte */}
                    <motion.div variants={itemVariants} className="info-section">
                      <div className="section-header">
                        <i className="bi bi-shield-check"></i>
                        <h3>3. Mot de passe</h3>
                      </div>
                      <div className="info-grid">
                        <div className="info-item" style={{ gridColumn: "1 / 2" }}>
                          <div className="info-label">
                            <i className="bi bi-lock me-2"></i>
                            Nouveau mot de passe
                          </div>
                          <Form.Item
                            name="newPassword"
                            rules={[
                              { min: 6, message: "Le mot de passe doit contenir au moins 6 caractères" },
                            ]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input.Password placeholder="Minimum 6 caractères" size="large" />
                          </Form.Item>
                        </div>

                        <div className="info-item" style={{ gridColumn: "2 / 3" }}>
                          <div className="info-label">
                            <i className="bi bi-lock me-2"></i>
                            Confirmez le mot de passe
                          </div>
                          <Form.Item
                            name="confirmPassword"
                            dependencies={["newPassword"]}
                            rules={[
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  if (!getFieldValue("newPassword") || getFieldValue("newPassword") === value) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(new Error("Les mots de passe ne correspondent pas"));
                                },
                              }),
                            ]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input.Password placeholder="Confirmez le mot de passe" size="large" />
                          </Form.Item>
                        </div>
                      </div>

                      <Form.Item shouldUpdate={(prev, curr) => prev.profession !== curr.profession}>
                        {({ getFieldValue }) =>
                          getFieldValue("profession") === "Autre" && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} style={{ marginTop: 16 }}>
                              <Form.Item name="profession_other" label="Précisez votre profession" rules={[{ required: true, message: "Veuillez préciser votre profession" }]} style={{ marginBottom: 0 }}>
                                <Input placeholder="Ex: Consultant juridique indépendant" size="large" />
                              </Form.Item>
                            </motion.div>
                          )
                        }
                      </Form.Item>
                    </motion.div>

                    {/* Section documents professionnels */}
                    <Form.Item shouldUpdate={(prev, curr) => prev.user_category !== curr.user_category}>
                      {({ getFieldValue }) =>
                        getFieldValue("user_category") === "professionnel" && (
                          <motion.div variants={itemVariants} className="info-section documents-section">
                            <div className="section-header">
                              <i className="bi bi-folder2-open"></i>
                              <h3>4. Documents professionnels</h3>
                            </div>
                            <Alert
                              message={
                                <div>
                                  <strong>Compte Professionnel :</strong> Ce type de compte nécessite la vérification de vos documents.
                                  <br />
                                  <small>Votre compte sera activé sous 48h après validation.</small>
                                </div>
                              }
                              type="info"
                              showIcon
                              icon={<FontAwesomeIcon icon={faInfoCircle} />}
                              style={{ marginBottom: 24 }}
                            />

                            <div style={{ marginBottom: 16 }}>
                              <ProfessionalDocumentUpload onDocumentsChange={handleProfessionalDocuments} />
                            </div>

                            <Divider />

                            {/* Section coordonnées bancaires */}
                            <div className="banking-section" style={{ marginTop: 24 }}>
                              <div className="section-header" style={{ marginBottom: 16 }}>
                                <i className="bi bi-bank2"></i>
                                <h3>Coordonnées bancaires</h3>
                              </div>
                              <div className="banking-info-box">
                                <div className="banking-warning" style={{ marginBottom: 20, padding: 12, backgroundColor: "#f0f9ff", borderRadius: 6, border: "1px solid #4bb0c0", display: "flex", gap: 12 }}>
                                  <i className="bi bi-info-circle" style={{ color: "#4bb0c0", fontSize: "1.1rem" }}></i>
                                  <span>Ces informations sont utilisées pour les remboursements et paiements</span>
                                </div>

                                <Button
                                  type={bankCoordinates.account_number ? "default" : "primary"}
                                  icon={<FontAwesomeIcon icon={faBank} />}
                                  size="large"
                                  style={{
                                    width: "100%",
                                    borderColor: bankCoordinates.account_number ? "#d9d9d9" : "#4BB0C0",
                                    backgroundColor: bankCoordinates.account_number ? "#fff" : "#4BB0C0",
                                    color: bankCoordinates.account_number ? "#4bb0c0" : "#fff",
                                    height: 48,
                                  }}
                                  onClick={() => setRibModalVisible(true)}
                                >
                                  {bankCoordinates.account_number ? "Modifier les coordonnées bancaires" : "Ajouter les coordonnées bancaires (RIB + BIC)"}
                                </Button>

                                {bankCoordinates.account_number && (
                                  <motion.div 
                                    initial={{ opacity: 0, scale: 0.95 }} 
                                    animate={{ opacity: 1, scale: 1 }} 
                                    style={{ 
                                      marginTop: 12, 
                                      padding: 16, 
                                      backgroundColor: "#f0f9ff", 
                                      border: "1px solid #4bb0c0", 
                                      borderRadius: 6 
                                    }}
                                  >
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                                      <i className="bi bi-check-circle-fill" style={{ color: "#4bb0c0" }}></i>
                                      <Text strong style={{ color: "#4bb0c0" }}>
                                        Coordonnées bancaires enregistrées
                                      </Text>
                                    </div>
                                    <Text code style={{ display: "block", marginTop: 8, fontSize: "0.95rem" }}>
                                      RIB: {formatRIB(bankCoordinates.account_number)}
                                    </Text>
                                    <Text code style={{ display: "block", marginTop: 4, fontSize: "0.95rem" }}>
                                      BIC: {bankCoordinates.bic_swift}
                                    </Text>
                                  </motion.div>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        )
                      }
                    </Form.Item>


                  {feedback && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{ marginBottom: 20 }}
                      >
                        <Alert
                          type={feedback.type}
                          message={feedback.message}
                          showIcon
                          closable
                          onClose={() => setFeedback(null)}
                        />
                      </motion.div>
                    )}
                    {/* Bouton de soumission */}
                    <motion.div variants={itemVariants} className="info-section" style={{ textAlign: "center", paddingTop: 24, borderTop: "1px solid #e0e0e0" }}>
                      <motion.button
                        type="button"
                        onClick={() => form.submit()}
                        disabled={loading}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        style={{
                          background: "linear-gradient(135deg, #4BB0C0 0%, #2A919D 100%)",
                          border: "none",
                          color: "white",
                          padding: "16px 48px",
                          fontSize: "16px",
                          fontWeight: "600",
                          borderRadius: "8px",
                          cursor: loading ? "not-allowed" : "pointer",
                          boxShadow: "0 4px 12px rgba(75, 176, 192, 0.3)",
                          minWidth: "200px",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        {loading ? (
                          <>
                            <span style={{ marginRight: 8 }}>Traitement en cours</span>
                            <motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} style={{ display: "inline-block" }}>
                              ⏳
                            </motion.span>
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 8 }} />
                            Finaliser l'inscription
                          </>
                        )}
                      </motion.button>

                      <Text type="secondary" style={{ display: "block", marginTop: 12 }}>
                        <a             
                        href="https://annonce-legale-officielle.fr/politique-de-confidentialite/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#4BB0C0", textDecoration: "underline" }}
                        >
                          Politique de confidentialité
                        </a>
                      </Text>
                    </motion.div>
                  </Form>
                </motion.div>
              ) : (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ textAlign: "center", padding: "40px 20px" }}>
                  <div style={{ marginBottom: 20 }}>
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} style={{ width: 40, height: 40, border: "3px solid #4BB0C0", borderTopColor: "transparent", borderRadius: "50%", margin: "0 auto" }} />
                  </div>
                  <Title level={4} style={{ color: "#4BB0C0" }}>
                    Chargement de vos données...
                  </Title>
                </motion.div>
              )}


            </div>
          </Modal>
        )}

        
      </AnimatePresence>

    {isModalVisible && showSuccessCard && (
        <Modal
        isOpen={isModalVisible}
        onRequestClose={() => {
          setShowSuccessCard(false);
          handleModalClose();
        }}
        contentLabel="Inscription réussie"
        className="user-validation-modal success-modal"
        overlayClassName="user-validation-overlay"   
        shouldCloseOnOverlayClick={false}   
        >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="success-card-container"
        >
          {/* En-tête de la carte */}
          <div className="modal-header-custom" style={{ background: "linear-gradient(135deg, #4BB0C0 0%, #2A919D 100%)" }}>
            <div className="header-content">
              <div className="user-avatar" style={{ backgroundColor: "rgba(255,255,255,0.2)" }}>
                <i className="bi bi-check-circle-fill" style={{ fontSize: "2rem", color: "white" }}></i>
              </div>
              <div className="header-info">
                <h2 className="modal-title" style={{ color: "white" }}>Inscription réussie !</h2>
                <div className="user-meta">
                  <span style={{ color: "rgba(255,255,255,0.9)" }}>Vérification email requise pour activer votre compte</span>
                </div>
              </div>
            </div>
          </div>

          {/* Corps de la carte */}
          <div style={{ padding: "30px", maxWidth: "600px", margin: "0 auto" }}>
            {/* Section d'alerte importante */}
            <div style={{
              backgroundColor: "#fff8e1",
              border: "2px solid #ffd54f",
              borderRadius: "8px",
              padding: "20px",
              marginBottom: "25px",
              position: "relative"
            }}>
              <div style={{
                position: "absolute",
                top: "-12px",
                left: "20px",
                backgroundColor: "#ffd54f",
                color: "#333",
                padding: "4px 12px",
                borderRadius: "4px",
                fontWeight: "bold",
                fontSize: "0.9rem"
              }}>
                ⚠️ ACTION REQUISE
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "15px" }}>
                <div style={{ color: "#d35400", fontSize: "1.5rem" }}>📧</div>
                <div>
                  <h4 style={{ margin: "0 0 10px 0", color: "#d35400" }}>
                  Confirmez votre adresse e-mail                  
                  </h4>
                  <p style={{ margin: "0", lineHeight: "1.5" }}>
                    Un email de vérification a été envoyé à :<br/>
                    <strong style={{ color: "#4BB0C0" }}>{registrationData?.email}</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Instructions étape par étape */}
            <div style={{ marginBottom: "30px" }}>
              <h3 style={{ color: "#2c3e50", marginBottom: "20px", textAlign: "center" }}>
                Récupérer votre annonce en suivant ces 2 étapes :
              </h3>
              
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "20px" }}>
                {/* Étape 1 */}
                <div style={{
                  backgroundColor: "#f8f9fa",
                  padding: "20px",
                  borderRadius: "8px",
                  border: "1px solid #e9ecef",
                  textAlign: "center"
                }}>
                  <div style={{
                    width: "50px",
                    height: "50px",
                    backgroundColor: "#4BB0C0",
                    color: "white",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.5rem",
                    margin: "0 auto 15px auto"
                  }}>
                    1
                  </div>
                  <h4 style={{ color: "#4BB0C0", marginBottom: "10px" }}>Déconnexion</h4>
                  <p style={{ fontSize: "0.9rem", color: "#666" }}>
                    <strong>Fermez cette fenêtre</strong> ou <strong>déconnectez-vous</strong> de votre session actuelle
                  </p>
                </div>

                {/* Étape 2 */}
                <div style={{
                  backgroundColor: "#f8f9fa",
                  padding: "20px",
                  borderRadius: "8px",
                  border: "1px solid #e9ecef",
                  textAlign: "center"
                }}>
                  <div style={{
                    width: "50px",
                    height: "50px",
                    backgroundColor: "#4BB0C0",
                    color: "white",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.5rem",
                    margin: "0 auto 15px auto"
                  }}>
                    2
                  </div>
                  <h4 style={{ color: "#4BB0C0", marginBottom: "10px" }}>Vérification</h4>
                  <p style={{ fontSize: "0.9rem", color: "#666" }}>
                    <strong>Cliquez sur le lien</strong> dans l'email que vous avez reçu dans votre boîte email
                  </p>
                </div>
              </div>
            </div>

            {/* Informations d'identification */}
            <div style={{
              backgroundColor: "#e8f4f8",
              padding: "20px",
              borderRadius: "8px",
              border: "1px solid #4BB0C0",
              marginBottom: "25px"
            }}>
              <h4 style={{ color: "#2c3e50", marginBottom: "15px", display: "flex", alignItems: "center", gap: "10px" }}>
                <i className="bi bi-key-fill"></i>
                Votre identifiant de connexion
              </h4>
                  <div style={{
                    display: "grid",
                    justifyContent: "center",
                    textAlign: "center"
                  }}>                
                  <div>

                  <div style={{
                    backgroundColor: "white",
                    padding: "10px 15px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                    fontWeight: "bold",
                    color: "#4BB0C0",
                    wordBreak: "break-all",
                    textAlign: "center"
                  }}>
                    {registrationData?.email}
                  </div>
                </div>
              </div>
              <p style={{ fontSize: "0.85rem", color: "#666", marginTop: "15px", marginBottom: "0" }}>
                <i className="bi bi-info-circle me-2"></i>
                Cet identifiant sera nécessaire après la vérification de votre email
              </p>
            </div>

            {/* Avertissement important */}
            <div style={{
              backgroundColor: "#ffeaea",
              padding: "15px",
              borderRadius: "6px",
              border: "1px solid #ff6b6b",
              marginBottom: "25px"
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <i className="bi bi-exclamation-triangle-fill" style={{ color: "#d32f2f", fontSize: "1.2rem" }}></i>
                <div>
                  <strong style={{ color: "#d32f2f" }}>IMPORTANT :</strong>
                  <p style={{ margin: "8px 0 0 0", color: "#d32f2f" }}>
                    <strong>Sans vérification email, vous ne pourrez pas :</strong>
                  </p>
                  <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px", color: "#d32f2f" }}>
                    <li>Consulter l'annonce que vous venez de créer</li>
                    <li>Télécharger votre attestation et facture</li>
                    <li>Accéder aux fonctionnalités de votre compte</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div style={{ display: "flex", justifyContent: "center", gap: "15px", marginTop: "30px" }}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setShowSuccessCard(false);
                  handleModalClose();
                  // Redirection vers la page de déconnexion si disponible
                  window.location.href = "/login"; // Ajustez selon votre route
                }}
                style={{
                  backgroundColor: "#4BB0C0",
                  color: "white",
                  border: "none",
                  padding: "12px 30px",
                  borderRadius: "6px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px"
                }}
              >
                <i className="bi bi-box-arrow-right"></i>
                Me déconnecter maintenant
              </motion.button>
            </div>

            {/* Lien de support */}
            <div style={{ textAlign: "center", marginTop: "20px", paddingTop: "20px", borderTop: "1px solid #eee" }}>
              <p style={{ fontSize: "0.85rem", color: "#666" }}>
                Vous n'avez pas reçu l'email ? Vérifiez vos spams ou
                <a href="/contact" style={{ color: "#4BB0C0", marginLeft: "5px", fontWeight: "bold" }}>
                  contactez notre support
                </a>
              </p>
            </div>
          </div>
        </motion.div>
      </Modal>
    )}
    </>
  );
};

export default AnonymousModalPaiement;
